import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../../observability/telemetry';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. If omitted, a built-in inline panel is
   *  shown with the error message + a reset button. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Route-level ErrorBoundary that catches render-time exceptions and
 * shows them on-screen instead of producing a blank page. Used by
 * AppShell to wrap <Outlet/> so a single bad screen can't take down
 * the whole SPA. The boundary uses `getDerivedStateFromError` to
 * switch into the error state, and `componentDidCatch` to log the
 * error to the console for developer inspection AND report it to
 * the telemetry backend.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Strip React's componentStack down to a few hundred chars so we
    // don't blow the payload size budget. Component names are
    // already useful for debugging.
    const stack =
      (info.componentStack ?? '').split('\n').slice(0, 6).join(' | ') || undefined;
    reportError(error, { feature: 'error-boundary', tags: { phase: stack ? 'render' : 'unknown' } });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <div
          role="alert"
          className="nx-error-boundary"
          style={{
            margin: 24,
            padding: 16,
            border: '1px solid rgba(255, 77, 106, 0.5)',
            borderRadius: 8,
            background: 'rgba(255, 77, 106, 0.08)',
            // Tokens, not hex: the pale text was the dark palette, so in
            // light mode the crash message was near-white on white.
            color: 'var(--nx-text, #e8ecf0)',
          }}
        >
          <h2 style={{ margin: '0 0 8px 0', fontSize: 18 }}>Something went wrong</h2>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              margin: '0 0 12px 0',
              fontSize: 12,
              color: 'var(--nx-error-text, #ffb5c2)',
              maxHeight: 240,
              overflow: 'auto',
            }}
          >
            {error.message}
            {'\n'}
            {error.stack ?? ''}
          </pre>
          <button
            type="button"
            onClick={this.reset}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid var(--nx-border, rgba(255, 255, 255, 0.15))',
              background: 'transparent',
              color: 'var(--nx-text, #e8ecf0)',
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}